import { Data } from "../types";

type MatchCardProps = {
  data: Data | null;
}

type TeamTone = "ct" | "t";

const normalizeSide = (value: string | undefined): TeamTone | null => {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  if (normalized === "ct" || normalized.includes("counter")) return "ct";
  if (normalized === "t" || normalized.includes("terror")) return "t";
  return null;
};

const buildFallbackTones = (teams: { name: string }[]) => {
  const names = Array.from(new Set(teams.map(team => team.name))).sort((a, b) => a.localeCompare(b));
  const tones = new Map<string, TeamTone>();
  for (let index = 0; index < names.length; index += 1) {
    const name = names[index];
    tones.set(name, index % 2 === 0 ? "ct" : "t");
  }
  return tones;
};

export default function MatchCard({data}: MatchCardProps) {
  const teams = Object.entries(data?.teams || {}).map(([name, team]) => ({
    name,
    score: team.score,
    startedOn: team.startedOn
  }));
  const fallbackTones = buildFallbackTones(teams);
  const sortedTeams = [...teams].sort((a, b) => b.score - a.score);
  const winner = sortedTeams[0];
  const loser = sortedTeams[1];
  const winnerTone = normalizeSide(winner?.startedOn) ?? fallbackTones.get(winner?.name ?? "") ?? "t";
  const loserTone = normalizeSide(loser?.startedOn) ?? fallbackTones.get(loser?.name ?? "") ?? "ct";

  return (
    <div className="rounded-[20px] border border-accent/35 bg-[linear-gradient(180deg,rgba(53,25,44,0.95),rgba(31,13,26,0.95))] p-5 shadow-[0_20px_40px_rgba(0,0,0,0.45)]">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[18px] font-bold">Analyzed match</div>
          <div className="mt-1 text-[13px] text-muted">{data?.map} - {data?.rounds.length} rounds</div>
        </div>
      </div>
      <div className="mt-[18px] grid gap-4">
        <div className="grid gap-2.5">
          <div className="flex justify-between items-center gap-2.5 font-semibold">
            <div className="flex items-center gap-2.5">
              <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-[12px] font-bold ${winnerTone === "ct" ? "bg-ct/80 text-ct" : "bg-t/80 text-t"}`} />
              {winner?.name ?? "TBD"}
            </div>
            <span className="text-accent text-[24px]">{winner?.score ?? 0}</span>
          </div>
          <div className="flex justify-between items-center gap-2.5 font-semibold">
            <div className="flex items-center gap-2.5">
              <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-[12px] font-bold ${loserTone === "ct" ? "bg-ct/80 text-ct" : "bg-t/80 text-t"}`} />
              {loser?.name ?? "TBD"}
            </div>
            <span className="text-accent text-[24px]">{loser?.score ?? 0}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
